import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  TableSortLabel,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import axios from "../../config/axios";
import { useGooglePicker } from "../../hooks/useGooglePicker";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { formatInTimeZone } from "date-fns-tz";

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    () => localStorage.getItem("rowsPerPage") || 25
  );
  const [totalProducts, setTotalProducts] = useState(0);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [formData, setFormData] = useState([
    { mfg_date: "", lot_number: "", tempId: uuidv4() },
  ]);
  const [mainFormData, setMainFormData] = useState({ name: "", brand: "" });
  const [metaTags, setMetaTags] = useState([]);
  const [threeField, setThreeField] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [threeFieldInput, setThreeFieldInput] = useState("");
  const [errors, setErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [orderBy, setOrderBy] = useState("latest_mfg_date");
  const [order, setOrder] = useState("desc");
  const [indexToRemove, setIndexToRemove] = useState(null);
  const [activePickerTempId, setActivePickerTempId] = useState(null);
  const searchInputRef = useRef(null);
  const dialogContentRef = useRef(null);

  const {
    gapiLoaded,
    pickerLoading,
    apiError: pickerApiError,
    createPicker,
  } = useGooglePicker({
    onFilePicked: (file, tempId) => {
      setSelectedFiles((prev) => {
        if (!prev.some((f) => f.id === file.id && f.tempId === tempId)) {
          return [...prev, { ...file, tempId }];
        }
        return prev;
      });
      setActivePickerTempId(null);

      handleFileAssignToLot(file, tempId);
    },
  });

  useEffect(() => {
    if (pickerApiError) {
      setError(pickerApiError);
    }
  }, [pickerApiError]);

  const fetchProducts = useCallback(async () => {
    try {
      setError(null);
      const effectiveRowsPerPage =
        rowsPerPage === "All" ? totalProducts : parseInt(rowsPerPage, 10);
      const response = await axios.get("/products", {
        params: {
          page: page + 1,
          limit: effectiveRowsPerPage,
          search,
          sortBy: orderBy,
          order,
        },
      });
      setProducts(response.data.products);
      setTotalProducts(response.data.pagination.total);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error(error.response?.data?.message || "Error fetching products");
      setError(error.response?.data?.message || "Error fetching products");
    }
  }, [page, rowsPerPage, search, orderBy, order, totalProducts]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const startEditing = async (product) => {
    try {
      setError(null);
      const response = await axios.get(`/products/${product.id}`);
      const productData = response.data;
      setMainFormData({
        name: productData.name || "",
        brand: productData.brand || "",
      });
      setMetaTags(
        productData.keyword_search_terms
          ? productData.keyword_search_terms.split(",").map((tag) => tag.trim())
          : []
      );
      setThreeField(
        productData.asin_upc_fnsku
          ? productData.asin_upc_fnsku.split(",").map((bar) => bar.trim())
          : []
      );
      const newFormData =
        productData.lots && productData.lots.length > 0
          ? productData.lots.map((lot) => ({
              mfg_date: lot.mfg_date || "",
              lot_number: lot.lot_number || "",
              tempId: uuidv4(),
              product_lot_id: lot.id,
            }))
          : [{ mfg_date: "", lot_number: "", tempId: uuidv4() }];
      setFormData(newFormData);

      setSelectedFiles(
        productData.files.map((file) => {
          const lot = productData.lots.find(
            (lot) => lot.id === file.product_lot_id
          );
          const tempId = lot
            ? newFormData.find((data) => data.product_lot_id === lot.id)
                ?.tempId || uuidv4()
            : uuidv4();
          return {
            id: file.file_id,
            name: file.filename,
            url: file.file_url,
            createdTime: file.created_at,
            tempId,
            product_lot_id: file.product_lot_id,
          };
        })
      );
      setSelectedProduct(product);
      setEditDialogOpen(true);
    } catch (error) {
      console.error("Error fetching product for edit:", error);
      toast.error(error.response?.data?.message || "Error fetching product");
      setError(error.response?.data?.message || "Error fetching product");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setError(null);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = event.target.value;
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setError(null);
    localStorage.setItem("rowsPerPage", newRowsPerPage);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
    setError(null);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const decodeTheLotNumber = (lotNumber) => {
    if (!/^\d{5,9}$/.test(lotNumber)) return "";

    const dayOfYear = parseInt(lotNumber.substring(0, 3), 10);
    const yearPart = parseInt(lotNumber.substring(3, 5), 10);
    const fullYear = 2000 + yearPart;

    // Check if year is leap
    const isLeap =
      (fullYear % 4 === 0 && fullYear % 100 !== 0) || fullYear % 400 === 0;

    // Check if dayOfYear is valid
    if (dayOfYear < 1 || dayOfYear > (isLeap ? 366 : 365)) {
      toast.error(
        `Invalid day for year ${fullYear}. ${
          isLeap
            ? "Leap year allows max 366 days."
            : "Not a leap year, max 365 days are there."
        }`
      );
      return "";
    }

    const date = new Date(Date.UTC(fullYear, 0, dayOfYear));

    return date.toISOString().split("T")[0];
  };

  const handleFileAssignToLot = (file, lotId) => {
    const isDuplicate = selectedFiles.some(
      (f) => f.id === file.id && f.product_lot_id !== lotId
    );

    if (isDuplicate) {
      toast.warning("This file is already in used.");
      return;
    }
    const lotIndex = formData.findIndex((f) => f.tempId === lotId);
    if (lotIndex !== -1) {
      const lotNumber = formData[lotIndex].lot_number;

      const fileNameNumber = file.name.replace(/\D/g, "");

      if (fileNameNumber !== lotNumber) {
        toast.warning(
          `Filename (${fileNameNumber}) does not match lot number (${lotNumber})`
        );
      }
    }
  };

  const handleMainInputChange = (e) => {
    const { name, value } = e.target;
    setMainFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === "lot_number") {
      const manufacturingDate = decodeTheLotNumber(value);
      setFormData((prev) =>
        prev.map((data, i) =>
          i === index
            ? { ...data, lot_number: value, mfg_date: manufacturingDate }
            : data
        )
      );
      if (manufacturingDate && errors[`mfg_date_${index}`]) {
        setErrors((prev) => ({ ...prev, [`mfg_date_${index}`]: "" }));
      }
    } else {
      setFormData((prev) =>
        prev.map((data, i) => (i === index ? { ...data, [name]: value } : data))
      );
    }
    if (errors[`${name}_${index}`]) {
      setErrors((prev) => ({ ...prev, [`${name}_${index}`]: "" }));
    }
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      if (!metaTags.includes(tagInput.trim())) {
        setMetaTags((prev) => [...prev, tagInput.trim()]);
        setTagInput("");
      }
    }
  };

  const handleThreeFieldInputChange = (e) => {
    setThreeFieldInput(e.target.value.toUpperCase());
  };

  const handleThreeFieldInputKeyDown = (e) => {
    if (e.key === "Enter" && threeFieldInput.trim()) {
      e.preventDefault();
      if (!threeField.includes(threeFieldInput.trim())) {
        setThreeField((prev) => [...prev, threeFieldInput.trim()]);
        setThreeFieldInput("");
      }
    }
  };

  const handleTagDelete = (tagToDelete) => {
    setMetaTags((prev) => prev.filter((tag) => tag !== tagToDelete));
  };

  const handleThreeFieldDelete = (fieldToDelete) => {
    setThreeField((prev) => prev.filter((bar) => bar !== fieldToDelete));
  };

  const removeFile = async (fileId, tempId) => {
    try {
      setError(null);
      const file = selectedFiles.find(
        (f) => f.id === fileId && f.tempId === tempId
      );
      if (file && file.product_lot_id) {
        await axios.delete(`/products/${selectedProduct.id}/files/${fileId}`);
      }
      setSelectedFiles((prev) =>
        prev.filter((f) => f.id !== fileId || f.tempId !== tempId)
      );
    } catch (error) {
      setError(error.response?.data?.message || "Error deleting file");
    }
  };

  const getSectionFiles = (tempId) =>
    selectedFiles.filter((file) => file.tempId === tempId);

  const validateForm = () => {
    const newErrors = {};
    const numericRegex = /^[0-9]+$/;
    if (!mainFormData.name.trim()) newErrors.name = "Product Name is required";
    if (!mainFormData.brand) newErrors.brand = "Brand is required";
    formData.forEach((data, index) => {
      if (
        !data.lot_number ||
        !numericRegex.test(data.lot_number) ||
        data.lot_number.length < 5 ||
        data.lot_number.length > 9
      ) {
        newErrors[`lot_number_${index}`] =
          "Lot Number is required, must be numeric, and between 5 to 9 digits";
        return;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitLoading(true);
      setError(null);
      const payload = {
        ...mainFormData,
        mfg_date_lot_number: formData.map((data) => ({
          id: data.product_lot_id || undefined,
          mfg_date: data.mfg_date,
          lot_number: data.lot_number,
        })),
        keyword_search_terms: metaTags.join(","),
        asin_upc_fnsku: threeField.join(","),
      };

      const response = await axios.put(
        `/products/${selectedProduct.id}`,
        payload
      );
      const productId = response.data.id;
      const lotMap = response.data.lots.reduce((acc, lot) => {
        const formLot = formData.find(
          (data) =>
            (data.product_lot_id && data.product_lot_id === lot.id) ||
            (data.mfg_date === lot.mfg_date &&
              data.lot_number === lot.lot_number)
        );
        if (formLot) acc[formLot.tempId] = lot.id;
        return acc;
      }, {});

      selectedFiles.filter((f) => f.product_lot_id).map((f) => f.id);
      const newFiles = selectedFiles.filter((f) => !f.product_lot_id);

      for (const file of newFiles) {
        const product_lot_id = lotMap[file.tempId];
        if (!product_lot_id) {
          console.warn(`No product_lot_id found for tempId: ${file.tempId}`);
          continue;
        }
        await axios.post(`/products/${productId}/files`, {
          file_id: file.id,
          filename: file.name,
          fileUrl: file.url,
          created_at: file.createdTime,
          product_lot_id,
        });
      }

      setEditDialogOpen(false);
      setSelectedProduct(null);
      setMainFormData({ name: "", brand: "" });
      setFormData([{ mfg_date: "", lot_number: "", tempId: uuidv4() }]);
      setMetaTags([]);
      setThreeField([]);
      setTagInput("");
      setThreeFieldInput("");
      setErrors({});
      setSelectedFiles([]);
      toast.success("Product updated successfully");
      fetchProducts();
    } catch (error) {
      // if (error.response?.data?.message === "This lot number already exist") {
      //   toast.error("This lot number already exist");
      //   return;
      // }
      // if (
      //   error.response?.data?.message ===
      //   "A product with the same name and brand already exists"
      // ) {
      //   toast.error("A product with the same name and brand already exists");
      //   return;
      // }
      // if (
      //   error.response?.data?.message ===
      //   "A product with the same asin / upc / fnsku already exists"
      // ) {
      //   toast.error(
      //     "A product with the same asin / upc / fnsku already exists"
      //   );
      //   return;
      // }
      toast.error(error.response?.data.message);
      setError(error.response?.data?.message || "Error updating product");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setSelectedProduct(null);
    setMainFormData({ name: "", brand: "" });
    setFormData([{ mfg_date: "", lot_number: "", tempId: uuidv4() }]);
    setMetaTags([]);
    setThreeField([]);
    setTagInput("");
    setThreeFieldInput("");
    setErrors({});
    setSelectedFiles([]);
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (indexToRemove !== null) {
      const tempIdToRemove = formData[indexToRemove].tempId;
      setFormData((prev) => prev.filter((_, i) => i !== indexToRemove));
      setSelectedFiles((prev) =>
        prev.filter((file) => file.tempId !== tempIdToRemove)
      );
      setIndexToRemove(null);
    } else if (productToDelete) {
      try {
        const response = await axios.delete(`/products/${productToDelete.id}`);
        setDeleteDialogOpen(false);
        setProductToDelete(null);
        toast.success(response.data.message);
        fetchProducts();
      } catch (error) {
        console.error("Error deleting product:", error);
        setError(error.response?.data?.message || "Error deleting product");
      }
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setIndexToRemove(null);
    setProductToDelete(null);
  };

  const openGooglePicker = (tempId) => {
    if (gapiLoaded && !pickerLoading && dialogContentRef.current) {
      setActivePickerTempId(tempId);
      createPicker(dialogContentRef, tempId, () => {
        setActivePickerTempId(null);
      });
    }
  };

  const handleAddFields = () => {
    setFormData((prev) => [
      ...prev,
      { mfg_date: "", lot_number: "", tempId: uuidv4() },
    ]);
  };

  const handleRemoveFields = (index) => {
    const hasFilledFields =
      formData[index].mfg_date ||
      formData[index].lot_number ||
      getSectionFiles(formData[index].tempId).length > 0;
    if (hasFilledFields) {
      setIndexToRemove(index);
      setDeleteDialogOpen(true);
    } else {
      setFormData((prev) => prev.filter((_, i) => i !== index));
      setSelectedFiles((prev) =>
        prev.filter((file) => file.tempId !== formData[index].tempId)
      );
    }
  };

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError("");
      }, 3000);
    }
  }, [error]);

  const pacificDate = formatInTimeZone(
    new Date(),
    "America/Los_Angeles",
    "yyyy-MM-dd"
  );

  return (
    <Box
      sx={{
        width: "100%",
        background: "#FFFFFF",
        borderRadius: "12px",
        padding: "30px",
        animation: "fadeIn 0.5s ease-in-out",
        "@media (max-width: 960px)": { padding: "20px" },
        "@media (max-width: 600px)": { padding: "10px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          "@media (max-width: 600px)": {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
          },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            color: "#26A69A",
            fontSize: "32px",
            fontWeight: 600,
            "@media (max-width: 960px)": { fontSize: "24px" },
            "@media (max-width: 600px)": { fontSize: "20px" },
          }}
        >
          Products
        </Typography>
        <Button
          onClick={() => navigate("/products/add")}
          sx={{
            background: "#26A69A",
            color: "#FFFFFF",
            padding: "10px 20px",
            borderRadius: "8px",
            fontWeight: 500,
            textTransform: "none",
            transition: "all 0.3s",
            "&:hover": { background: "#009688", transform: "scale(1.02)" },
            "@media (max-width: 600px)": {
              width: "100%",
              padding: "8px 16px",
              fontSize: "14px",
            },
          }}
        >
          Add Product
        </Button>
      </Box>

      {/* {!dialogContentRef && error && (
        <Box
          sx={{
            background: "#FF6F61",
            color: "#FFFFFF",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            animation: "slideIn 0.3s ease-in-out",
            "@media (max-width: 600px)": { fontSize: "14px", padding: "8px" },
          }}
        >
          <ErrorIcon sx={{ marginRight: "8px" }} />
          {error}
        </Box>
      )} */}

      <TextField
        inputRef={searchInputRef}
        fullWidth
        placeholder="Search products..."
        value={search}
        onChange={handleSearch}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton onClick={() => setSearch("")} edge="end">
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          mb: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            background: "rgba(255, 255, 255, 0.8)",
            "&:hover fieldset": { borderColor: "#26A69A" },
            "&.Mui-focused fieldset": { borderColor: "#26A69A" },
            "@media (max-width: 600px)": { fontSize: "14px", padding: "6px" },
          },
        }}
      />

      <TableContainer
        component={Paper}
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
          "@media (max-width: 960px)": { overflowX: "auto" },
          "@media (max-width: 600px)": { overflowX: "auto", fontSize: "14px" },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#26A69A",
                  "@media (max-width: 600px)": {
                    padding: "4px",
                    fontSize: "14px",
                  },
                }}
              >
                <TableSortLabel
                  active={orderBy === "latest_mfg_date"}
                  direction={orderBy === "latest_mfg_date" ? order : "asc"}
                  onClick={() => handleRequestSort("latest_mfg_date")}
                >
                  Mfg Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#26A69A",
                  "@media (max-width: 600px)": {
                    padding: "4px",
                    fontSize: "14px",
                  },
                }}
              >
                <TableSortLabel
                  active={orderBy === "latest_lot_number"}
                  direction={orderBy === "latest_lot_number" ? order : "asc"}
                  onClick={() => handleRequestSort("latest_lot_number")}
                >
                  Lot Number
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#26A69A",
                  "@media (max-width: 600px)": {
                    padding: "4px",
                    fontSize: "14px",
                  },
                }}
              >
                <TableSortLabel
                  active={orderBy === "brand"}
                  direction={orderBy === "brand" ? order : "asc"}
                  onClick={() => handleRequestSort("brand")}
                >
                  Brand
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#26A69A",
                  "@media (max-width: 600px)": {
                    padding: "4px",
                    fontSize: "14px",
                  },
                }}
              >
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  Product Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#26A69A",
                  "@media (max-width: 600px)": {
                    padding: "4px",
                    fontSize: "14px",
                  },
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow
                key={product.id}
                sx={{
                  "&:hover": { bgcolor: "#E0F2F1" },
                  transition: "background 0.2s",
                  "@media (max-width: 600px)": {
                    "& td": { padding: "4px", fontSize: "14px" },
                  },
                }}
              >
                <TableCell>
                  {new Date(product.latest_mfg_date).toLocaleDateString()}
                </TableCell>
                <TableCell>{product.latest_lot_number}</TableCell>
                <TableCell>{product.brand}</TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleDeleteClick(product)}
                    sx={{ "@media (max-width: 600px)": { padding: "4px" } }}
                  >
                    <DeleteIcon sx={{ color: "#FF6F61" }} />
                  </IconButton>
                  <IconButton
                    onClick={() => startEditing(product)}
                    sx={{ "@media (max-width: 600px)": { padding: "4px" } }}
                  >
                    <EditIcon sx={{ color: "#263238" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 250]}
          component="div"
          count={totalProducts}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiTablePagination-selectLabel": { color: "#263238" },
            "& .MuiTablePagination-displayedRows": { color: "#263238" },
            "& .MuiTablePagination-select": { color: "#263238" },
            "& .MuiIconButton-root": { color: "#26A69A" },
            "@media (max-width: 600px)": {
              "& .MuiTablePagination-selectLabel": { fontSize: "12px" },
              "& .MuiTablePagination-displayedRows": { fontSize: "12px" },
              "& .MuiTablePagination-select": { fontSize: "12px" },
              "& .MuiIconButton-root": { padding: "4px" },
            },
          }}
        />
      </TableContainer>

      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            background: "#FFFFFF",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            padding: "20px",
            animation: "fadeIn 0.5s ease-in-out",
            "@media (max-width: 960px)": { width: "90%", margin: "0 auto" },
            "@media (max-width: 600px)": {
              width: "100%",
              margin: "0 auto",
              padding: "10px",
            },
          },
        }}
      >
        <h1
          style={{
            color: "#263238",
            fontSize: "28px",
            fontWeight: 600,
            marginTop: "-5px",
            marginBottom: "-3px",
            textAlign: "center",
          }}
        >
          Edit Product
        </h1>
        <DialogContent ref={dialogContentRef}>
          {/* {error && (
            <Box
              sx={{
                background: "#FF6F61",
                color: "#FFFFFF",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                animation: "slideIn 0.3s ease-in-out",
                "@media (max-width: 600px)": {
                  fontSize: "14px",
                  padding: "8px",
                },
              }}
            >
              <ErrorIcon sx={{ marginRight: "8px" }} />
              {error}
            </Box>
          )} */}
          <form onSubmit={handleSaveEdit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              <TextField
                label="Brand"
                name="brand"
                value={mainFormData.brand}
                onChange={handleMainInputChange}
                error={!!errors.brand}
                helperText={
                  errors.brand && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#FF6F61",
                      }}
                    >
                      <ErrorIcon
                        style={{ fontSize: "16px", marginRight: "4px" }}
                      />
                      {errors.brand}
                    </span>
                  )
                }
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "8px",
                    background: "#F5F5F5",
                    transition: "all 0.3s",
                    marginTop: "8px",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#26A69A",
                    boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#263238",
                    fontWeight: 500,
                    marginTop: "8px",
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
                }}
              />
              <TextField
                label="Product Name"
                name="name"
                value={mainFormData.name}
                onChange={handleMainInputChange}
                error={!!errors.name}
                helperText={
                  errors.name && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#FF6F61",
                      }}
                    >
                      <ErrorIcon
                        style={{ fontSize: "16px", marginRight: "4px" }}
                      />
                      {errors.name}
                    </span>
                  )
                }
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "8px",
                    background: "#F5F5F5",
                    transition: "all 0.3s",
                    marginTop: "8px",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#26A69A",
                    boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#263238",
                    fontWeight: 500,
                    marginTop: "8px",
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
                }}
              />
              <div style={{ gridColumn: "span 2", marginTop: "-15px" }}>
                <h3
                  style={{
                    color: "#263238",
                    fontSize: "18px",
                    fontWeight: 500,
                    marginBottom: "0px",
                  }}
                >
                  ASIN / UPC / FNSKU
                </h3>
                {threeField.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "3px",
                      background: "#F5F5F5",
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  >
                    {threeField.map((bar) => (
                      <Chip
                        key={bar}
                        label={bar}
                        onDelete={() => handleThreeFieldDelete(bar)}
                        style={{
                          background: "#26A69A",
                          color: "#FFFFFF",
                          transform: "scale(1)",
                          transition: "transform 0.2s",
                        }}
                        sx={{ "&:hover": { transform: "scale(1.05)" } }}
                      />
                    ))}
                  </div>
                )}
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  label="Add (press Enter)"
                  value={threeFieldInput.toUpperCase()}
                  onChange={handleThreeFieldInputChange}
                  onKeyDown={handleThreeFieldInputKeyDown}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "8px",
                      background: "#F5F5F5",
                      transition: "all 0.3s",
                    },
                    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#26A69A",
                      boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#263238",
                      fontWeight: 500,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
                  }}
                />
              </div>
              <div style={{ gridColumn: "span 2", marginTop: "-15px" }}>
                <h3
                  style={{
                    color: "#263238",
                    fontSize: "18px",
                    fontWeight: 500,
                    marginBottom: "0px",
                  }}
                >
                  Keyword Search Terms
                </h3>
                {metaTags.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "3px",
                      background: "#F5F5F5",
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  >
                    {metaTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleTagDelete(tag)}
                        style={{
                          background: "#26A69A",
                          color: "#FFFFFF",
                          transform: "scale(1)",
                          transition: "transform 0.2s",
                        }}
                        sx={{ "&:hover": { transform: "scale(1.05)" } }}
                      />
                    ))}
                  </div>
                )}
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  label="Add tags (press Enter)"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyDown={handleTagInputKeyDown}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "8px",
                      background: "#F5F5F5",
                      transition: "all 0.3s",
                    },
                    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#26A69A",
                      boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#263238",
                      fontWeight: 500,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
                  }}
                />
                <Box
                  sx={{
                    minWidth: "200px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexShrink: 0,
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <Button
                    onClick={handleAddFields}
                    sx={{
                      background: "#26A69A",
                      color: "#FFFFFF",
                      borderRadius: "12px",
                      fontWeight: 600,
                      textTransform: "none",
                      padding: "12px 24px",
                      marginTop: "16px",
                      fontSize: "16px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      transition: "all 0.3s",
                      "&:hover": {
                        backgroundColor: "#219187",
                      },
                      "@media (max-width: 600px)": {
                        padding: "10px 20px",
                        fontSize: "14px",
                      },
                    }}
                  >
                    + Add New Lot
                  </Button>
                  <div
                    style={{
                      gridColumn: "span 2",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "16px",
                      marginTop: "24px",
                    }}
                  >
                    <Button
                      onClick={handleCancelEdit}
                      style={{
                        background: "#FFFFFF",
                        color: "#263238",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        border: "2px solid #263238",
                        fontWeight: 500,
                        textTransform: "none",
                        transition: "all 0.3s",
                      }}
                      sx={{
                        "&:hover": {
                          background: "#263238",
                          color: "#FFFFFF",
                          transform: "scale(1.02)",
                        },
                        "@media (max-width: 600px)": {
                          width: "90%",
                          marginBottom: "8px",
                          justifyContent: "center",
                        },
                        "@media (max-width: 320px)": {
                          width: "50%",
                          marginBottom: "8px",
                          display: "flex",
                          justifyContent: "center",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitLoading}
                      style={{
                        background: "#FF6F61",
                        color: "#FFFFFF",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        fontWeight: 500,
                        textTransform: "none",
                        transition: "all 0.3s",
                      }}
                      sx={{
                        "&:hover": {
                          background: "#F4511E",
                          transform: "scale(1.02)",
                        },
                        "&:disabled": {
                          background: "#B0BEC5",
                          cursor: "not-allowed",
                        },
                        "@media (max-width: 600px)": {
                          width: "90%",
                          justifyContent: "center",
                        },
                        "@media (max-width: 320px)": {
                          width: "60%",
                          marginBottom: "8px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        },
                      }}
                    >
                      {submitLoading ? "Updating..." : "Update Product"}
                    </Button>
                  </div>
                </Box>
                <hr
                  style={{
                    border: "none",
                    height: "2px",
                    backgroundColor: "#ccc",
                    marginTop: "20px",
                  }}
                />
              </div>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "flex-start",
                  width: "204%",
                  maxWidth: "204%",
                  overflowX: "hidden",
                }}
              >
                {formData
                  .slice()
                  .reverse()
                  .map((data, index) => (
                    <Box
                      key={data.tempId}
                      sx={{
                        width: "100%",
                        flexShrink: 0,
                        border: "1px solid #E0E0E0",
                        borderRadius: "16px",
                        padding: "32px",
                        backgroundColor: "#FFFFFF",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                        marginBottom: "20px",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          marginBottom: 3,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: "2px solid #26A69A",
                            paddingBottom: "8px",
                            marginTop: "-15px",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              color: "#263238",
                              fontWeight: 600,
                            }}
                          >
                            Lot Entry {formData.length - index}
                          </Typography>

                          {index < formData.length - 1 && (
                            <IconButton
                              onClick={() =>
                                handleRemoveFields(formData.length - 1 - index)
                              }
                              sx={{
                                color: "#FF6F61",
                                transition: "transform 0.2s",
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                          gap: 2,
                          marginBottom: 3,
                        }}
                      >
                        <TextField
                          label="Lot Number"
                          type="number"
                          name="lot_number"
                          value={data.lot_number}
                          onChange={(e) =>
                            handleInputChange(e, formData.length - 1 - index)
                          }
                          error={
                            !!errors[
                              `lot_number_${formData.length - 1 - index}`
                            ]
                          }
                          helperText={
                            errors[
                              `lot_number_${formData.length - 1 - index}`
                            ] && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#FF6F61",
                                }}
                              >
                                <ErrorIcon
                                  sx={{ fontSize: "16px", marginRight: "4px" }}
                                />
                                {
                                  errors[
                                    `lot_number_${formData.length - 1 - index}`
                                  ]
                                }
                              </Box>
                            )
                          }
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "8px",
                              backgroundColor: "#F9FAFB",
                            },
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#26A69A",
                              boxShadow: "0 0 8px rgba(38, 166, 154, 0.2)",
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#26A69A",
                            },
                          }}
                        />
                        <TextField
                          label="Manufacturing Date"
                          type="date"
                          name="mfg_date"
                          value={data.mfg_date}
                          onChange={(e) =>
                            handleInputChange(e, formData.length - 1 - index)
                          }
                          inputProps={{ max: pacificDate, readonly: true }}
                          InputLabelProps={{ shrink: true }}
                          error={
                            !!errors[`mfg_date_${formData.length - 1 - index}`]
                          }
                          disabled
                          helperText={
                            errors[
                              `mfg_date_${formData.length - 1 - index}`
                            ] && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#FF6F61",
                                }}
                              >
                                <ErrorIcon
                                  sx={{ fontSize: "16px", marginRight: "4px" }}
                                />
                                {
                                  errors[
                                    `mfg_date_${formData.length - 1 - index}`
                                  ]
                                }
                              </Box>
                            )
                          }
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "8px",
                              backgroundColor: "#F9FAFB",
                            },
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#26A69A",
                              boxShadow: "0 0 8px rgba(38, 166, 154, 0.2)",
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#26A69A",
                            },
                          }}
                        />
                        <Box
                          onClick={() => {
                            if (getSectionFiles(data.tempId).length === 1) {
                              toast.warning(
                                "You cannot select more than one file"
                              );
                            }
                          }}
                          sx={{ width: "100%" }}
                        >
                          <Button
                            onClick={() => openGooglePicker(data.tempId)}
                            disabled={
                              !gapiLoaded ||
                              (pickerLoading &&
                                activePickerTempId !== data.tempId) ||
                              getSectionFiles(data.tempId).length === 1
                            }
                            fullWidth
                            sx={{
                              backgroundColor: "#26A69A",
                              color: "#FFFFFF",
                              borderRadius: "8px",
                              padding: "10px 24px",
                              textTransform: "none",
                              fontSize: "14px",
                              fontStretch: "100%",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              height: "55px",
                              maxWidth: "100%",
                              "&:hover": {
                                backgroundColor: "#219187",
                              },
                            }}
                          >
                            {pickerLoading && activePickerTempId === data.tempId
                              ? "Loading..."
                              : "Link Results"}
                          </Button>
                        </Box>
                      </Box>

                      {/* <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#37474F", fontWeight: 600 }}
                      >
                        Upload Files for Lot {data.lot_number || index + 1}
                      </Typography>
                    </Box> */}

                      {getSectionFiles(data.tempId).length > 0 && (
                        <TableContainer
                          sx={{
                            borderRadius: "8px",
                            overflow: "hidden",
                            backgroundColor: "#F9FAFB",
                          }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{ fontWeight: 600, color: "#455A64" }}
                                >
                                  File Name
                                </TableCell>
                                <TableCell
                                  sx={{ fontWeight: 600, color: "#455A64" }}
                                >
                                  Created Time
                                </TableCell>
                                <TableCell
                                  sx={{ fontWeight: 600, color: "#455A64" }}
                                >
                                  Actions
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {getSectionFiles(data.tempId).map((file) => (
                                <TableRow
                                  key={`${file.id}-${file.tempId}`}
                                  sx={{
                                    "&:hover": { backgroundColor: "#E0F2F1" },
                                    transition: "background 0.2s",
                                  }}
                                >
                                  <TableCell>{file.name}</TableCell>
                                  <TableCell>
                                    {new Date(file.createdTime).toLocaleString(
                                      "en-GB",
                                      {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                      }
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      size="small"
                                      sx={{
                                        color: "#FF6F61",
                                        textTransform: "none",
                                      }}
                                      onClick={() =>
                                        removeFile(file.id, file.tempId)
                                      }
                                    >
                                      Remove
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  ))}
              </Box>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "16px",
            "@media (max-width: 960px)": { width: "80%", margin: "0 auto" },
            "@media (max-width: 600px)": {
              width: "90%",
              margin: "0 auto",
              padding: "8px",
            },
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h6"
            sx={{
              color: "#1e40af",
              fontSize: "20px",
              fontWeight: 600,
              "@media (max-width: 600px)": { fontSize: "16px" },
            }}
          >
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              color: "#263238",
              "@media (max-width: 600px)": { fontSize: "14px" },
            }}
          >
            {indexToRemove !== null ? (
              <>
                Are you sure you want to delete Lot{" "}
                <b>
                  {formData[indexToRemove]?.lot_number || indexToRemove + 1}
                </b>{" "}
                and its associated files? This action cannot be undone.
              </>
            ) : (
              <>
                Are you sure you want to delete the product{" "}
                <b>{productToDelete?.name}</b>? This action cannot be undone.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              background: "#FFFFFF",
              color: "#263238",
              padding: "10px 20px",
              borderRadius: "8px",
              border: "2px solid #263238",
              fontWeight: 500,
              textTransform: "none",
              transition: "all 0.3s",
              "&:hover": {
                background: "#263238",
                color: "#FFFFFF",
                transform: "scale(1.02)",
              },
              "@media (max-width: 600px)": {
                padding: "6px 12px",
                fontSize: "14px",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{
              background: "#FF6F61",
              color: "#FFFFFF",
              padding: "10px 20px",
              borderRadius: "8px",
              fontWeight: 500,
              textTransform: "none",
              transition: "all 0.3s",
              "&:hover": { background: "#F4511E", transform: "scale(1.02)" },
              "@media (max-width: 600px)": {
                padding: "6px 12px",
                fontSize: "14px",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
